import { createAsyncThunk } from '@reduxjs/toolkit';

import SocialInboxClient from 'api/socialInboxClient';
import { notifyError, notifySuccess } from 'utils/notifyConfig';

export const getPlatforms = createAsyncThunk(
  'social/platforms',
  async (_, { rejectWithValue }) => {
    try {
      const data = await SocialInboxClient.get('/social/platforms');
      return data;
    } catch (error) {
      if (error.status !== 404) {
        notifyError(error.message || 'Failed to get platforms');
      }
      return rejectWithValue(error.message);
    }
  }
);

export const getSocialPermissionsByRole = createAsyncThunk(
  'social/getPermissionsByRole',
  async ({ roleName }, { rejectWithValue }) => {
    try {
      const data = await SocialInboxClient.get(
        `/social-permissions/role/${encodeURIComponent(roleName)}`
      );
      return { roleName, data };
    } catch (error) {
      if (error.status !== 404) {
        notifyError(error.message || 'Failed to get permissions');
      }
      return rejectWithValue(error.message);
    }
  }
);

export const getSocialPermissionsByTeam = createAsyncThunk(
  'social/getPermissionsByTeam',
  async ({ teamId }, { rejectWithValue }) => {
    try {
      const data = await SocialInboxClient.get(
        `/social-permissions/team/${encodeURIComponent(teamId)}`
      );
      return { teamId, data };
    } catch (error) {
      if (error.status !== 404) {
        notifyError(error.message || 'Failed to get permissions');
      }
      return rejectWithValue(error.message);
    }
  }
);

export const getSocialPermissionsByUser = createAsyncThunk(
  'social/getPermissionsByUser',
  async ({ userId }, { rejectWithValue }) => {
    try {
      const data = await SocialInboxClient.get(
        `/social-permissions/user/${encodeURIComponent(userId)}`
      );
      return { userId, data };
    } catch (error) {
      if (error.status !== 404) {
        notifyError(error.message || 'Failed to get permissions');
      }
      return rejectWithValue(error.message);
    }
  }
);

export const getAllPermissionsByAccessType = createAsyncThunk(
  'social/getAllPermissionsByAccessType',
  async ({ accessType }, { rejectWithValue }) => {
    try {
      const data = await SocialInboxClient.get('/social-permissions', {
        params: { accessType },
      });
      return { accessType, data };
    } catch (error) {
      if (error.status !== 404) {
        notifyError(error.message || 'Failed to get permissions');
      }
      return rejectWithValue(error.message);
    }
  }
);

export const getSocialPermissions = createAsyncThunk(
  'social/getPermissions',
  async ({ accessType, targetId }, { rejectWithValue }) => {
    try {
      const data = await SocialInboxClient.get('/social-permissions', {
        params: { accessType, targetId },
      });
      return { targetId, permissions: data };
    } catch (error) {
      notifyError(error.message || 'Failed to get permissions');
      return rejectWithValue(error.message);
    }
  }
);

export const setSocialPermissions = createAsyncThunk(
  'social/setPermissions',
  async (
    { accessType, targetId, permissions, permissionId, isActive = true },
    { rejectWithValue }
  ) => {
    try {
      let data;

      if (permissionId) {
        data = await SocialInboxClient.put(`/social-permissions/${permissionId}`, {
          permissions,
          isActive,
        });
      } else {
        data = await SocialInboxClient.post('/social-permissions', {
          accessType,
          targetId,
          permissions,
        });
      }

      notifySuccess('Permissions updated successfully');
      return { targetId, permissions: data };
    } catch (error) {
      notifyError(error.message || 'Failed to update permissions');
      return rejectWithValue(error.message);
    }
  }
);

export const togglePermissionStatus = createAsyncThunk(
  'social/togglePermissionStatus',
  async ({ permissionId, isActive, accessType }, { rejectWithValue }) => {
    try {
      const data = await SocialInboxClient.put(`/social-permissions/${permissionId}`, {
        isActive,
      });
      notifySuccess(`Permission ${isActive ? 'activated' : 'disabled'} successfully`);
      return { permissionId, isActive, accessType, data };
    } catch (error) {
      notifyError(error.message || 'Failed to update permission status');
      return rejectWithValue(error.message);
    }
  }
);

export const getMyEffectivePermissions = createAsyncThunk(
  'social/getMyEffectivePermissions',
  async (_, { rejectWithValue }) => {
    try {
      const data = await SocialInboxClient.get('/social-permissions/me/effective');
      return data;
    } catch (error) {
      if (error.status !== 404) {
        notifyError(error.message || 'Failed to get permissions');
      }
      return rejectWithValue(error.message);
    }
  }
);

export const getConversations = createAsyncThunk(
  'social/getConversations',
  async ({ platform, search }, { rejectWithValue }) => {
    try {
      const params = { platform };

      if (search && search.trim()) {
        params.search = search.trim();
      }

      const data = await SocialInboxClient.get('/social/conversations', {
        params,
      });
      return { platform, ...data };
    } catch (error) {
      if (error.status !== 404) {
        notifyError(error.message || 'Failed to get conversations');
      }
      return rejectWithValue(error.message);
    }
  }
);

export const getFacebookConversations = createAsyncThunk(
  'social/getFacebookConversations',
  async ({ limit = 10, after = null, search = '', append = false }, { rejectWithValue }) => {
    try {
      const params = { limit };

      if (after) {
        params.after = after;
      }

      if (search && search.trim()) {
        params.search = search.trim();
      }

      const data = await SocialInboxClient.get('/facebook/conversations', {
        params,
      });

      return {
        data: data.data || [],
        pagination: data.pagination || {},
        append,
      };
    } catch (error) {
      if (error.status !== 404) {
        notifyError(error.message || 'Failed to get Facebook conversations');
      }
      return rejectWithValue(error.message);
    }
  }
);

export const getFacebookMessages = createAsyncThunk(
  'social/getFacebookMessages',
  async (
    { userId, limit = 20, before = null, after = null, direction = 'initial' },
    { rejectWithValue }
  ) => {
    try {
      const params = { limit };

      if (before) {
        params.before = before;
      }

      if (after) {
        params.after = after;
      }

      const data = await SocialInboxClient.get(`/facebook/users/${userId}/messages`, {
        params,
      });
      const messages = data.data || [];

      return {
        data: messages,
        pagination: data.pagination || {},
        direction,
        userId,
      };
    } catch (error) {
      if (error.status !== 404) {
        notifyError(error.message || 'Failed to get Facebook messages');
      }
      return rejectWithValue(error.message);
    }
  }
);

export const getInstagramConversations = createAsyncThunk(
  'social/getInstagramConversations',
  async (
    { limit = 10, after = null, before = null, search = '', append = false },
    { rejectWithValue }
  ) => {
    try {
      const params = { limit };

      if (after) {
        params.after = after;
      }

      if (before) {
        params.before = before;
      }

      if (search && search.trim()) {
        params.search = search.trim();
      }

      const data = await SocialInboxClient.get('/facebook/instagram/conversations', {
        params,
      });

      return {
        data: data.data || [],
        pagination: data.pagination || {},
        append,
      };
    } catch (error) {
      if (error.status !== 404) {
        notifyError(error.message || 'Failed to get Instagram conversations');
      }
      return rejectWithValue(error.message);
    }
  }
);

export const getInstagramMessages = createAsyncThunk(
  'social/getInstagramMessages',
  async (
    { userId, limit = 20, before = null, after = null, direction = 'initial' },
    { rejectWithValue }
  ) => {
    try {
      const params = { limit };

      if (before) {
        params.before = before;
      }

      if (after) {
        params.after = after;
      }

      const data = await SocialInboxClient.get(
        `/facebook/instagram/users/${userId}/messages`,
        {
          params,
        }
      );
      const messages = data.data || [];

      return {
        data: messages,
        pagination: data.pagination || {},
        direction,
        userId,
      };
    } catch (error) {
      if (error.status !== 404) {
        notifyError(error.message || 'Failed to get Instagram messages');
      }
      return rejectWithValue(error.message);
    }
  }
);

export const getMessages = createAsyncThunk(
  'social/getMessages',
  async ({ conversationId, limit = 10, offset = 0 }, { rejectWithValue }) => {
    try {
      const params = { limit, offset };
      const data = await SocialInboxClient.get(
        `/social/conversations/${encodeURIComponent(conversationId)}/messages`,
        { params }
      );
      return { conversationId, limit, offset, ...data };
    } catch (error) {
      if (error.status !== 404) {
        notifyError(error.message || 'Failed to get messages');
      }
      return rejectWithValue(error.message);
    }
  }
);

export const sendMessage = createAsyncThunk(
  'social/sendMessage',
  async (
    { conversationId, senderId, platform, messageText, subject, file, fileType },
    { rejectWithValue }
  ) => {
    try {
      const isEmailPlatform = platform === 'gmail' || platform === 'imap';

      const identifier = isEmailPlatform ? senderId : conversationId;

      if (!identifier) {
        const requiredParam = isEmailPlatform ? 'senderId' : 'conversationId';
        throw new Error(`${requiredParam} is required for platform: ${platform}`);
      }

      let requestData;

      // If file is provided, use FormData
      if (file && fileType) {
        const formData = new FormData();
        // Only add messageText if it's not empty
        if (messageText && messageText.trim()) {
          formData.append('messageText', messageText);
        }
        if (isEmailPlatform && subject && subject.trim()) {
          formData.append('subject', subject);
        }
        formData.append('files', file);
        formData.append('fileType', fileType);
        requestData = formData;
      } else {
        // Otherwise use JSON
        requestData = { messageText };
        if (isEmailPlatform && subject && subject.trim()) {
          requestData.subject = subject;
        }
      }

      const data = await SocialInboxClient.post(
        `/social/conversations/${encodeURIComponent(identifier)}/messages?platform=${encodeURIComponent(platform)}`,
        requestData,
        { 'Content-Type': 'multipart/form-data' }
      );
      return data;
    } catch (error) {
      let errorMessage = error.message || 'Failed to send message';
      if (error.response?.status === 413 || error.status === 413) {
        errorMessage = 'File is too large. Please select a smaller file.';
      }
      notifyError(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const markMessageAsRead = createAsyncThunk(
  'social/markMessageAsRead',
  async ({ messageId }, { rejectWithValue }) => {
    try {
      await SocialInboxClient.patch(`/social/messages/${encodeURIComponent(messageId)}/read`);
      return { messageId };
    } catch (error) {
      console.error('Failed to mark message as read:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Mark all messages in a conversation as read
 * @param {Object} params - Parameters object
 * @param {string} params.conversationId - Conversation ID to mark as read
 * @returns {Promise<Object>} Object containing the conversationId
 */
export const markConversationAsRead = createAsyncThunk(
  'social/markConversationAsRead',
  async ({ conversationId }, { rejectWithValue }) => {
    try {
      await SocialInboxClient.patch(
        `/social/conversations/${encodeURIComponent(conversationId)}/read`
      );
      return { conversationId };
    } catch (error) {
      // Silently fail - don't show error notification for read status
      console.error('Failed to mark conversation as read:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Get total unread count across all platforms
 * @returns {Promise<Object>} Unread count data
 */
export const getUnreadCount = createAsyncThunk(
  'social/getUnreadCount',
  async (_, { rejectWithValue }) => {
    try {
      const data = await SocialInboxClient.get('/social/unread-count');
      return data;
    } catch (error) {
      if (error.status !== 404) {
        notifyError(error.message || 'Failed to get unread count');
      }
      return rejectWithValue(error.message);
    }
  }
);

export const downloadWhatsAppMedia = createAsyncThunk(
  'social/downloadWhatsAppMedia',
  async ({ mediaId, filename = 'download' }, { rejectWithValue }) => {
    try {
      const response = await SocialInboxClient.get(
        `/whatsapp/media/${mediaId}`,
        {
          responseType: 'blob',
        },
        null
      );

      const blob = new Blob([response], { type: response.type || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      notifySuccess('File downloaded successfully');
      return { mediaId, filename };
    } catch (error) {
      console.error('Failed to download WhatsApp media:', error);
      notifyError('Failed to download file. Please try again.');
      return rejectWithValue(error.message);
    }
  }
);

export const PERMISSION_TYPES = {
  none: 'none',
  view: 'view_only',
  reply: 'view_and_answer',
};

export const VALID_PLATFORMS = ['facebook', 'instagram', 'whatsapp', /* 'gmail', */ 'imap'];
